<template>
  <div>
    <v-row dense class="no-print">
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="formData.tanggal_awal"
          type="date"
          label="Dari Tanggal"
          outlined
          clearable
          dense
          autofocus
          @keydown.enter="fetchLaporan"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="formData.tanggal_akhir"
          type="date"
          label="Sampai Tanggal"
          outlined
          clearable
          dense
          @keydown.enter="fetchLaporan"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="4">
        <v-select
          v-model="formData.spk"
          :items="spkOptions"
          label="SPK"
          outlined
          dense
          clearable
        ></v-select>
      </v-col>
      <v-col cols="12" sm="4">
        <v-btn
          color="primary"
          @click="fetchLaporan"
          :disabled="!formData.tanggal_awal || !formData.tanggal_akhir"
        >
          Cari
        </v-btn>
      </v-col>
    </v-row>

    <v-row
      class="fill-height"
      align-content="center"
      justify="center"
      v-if="state.loading"
    >
      <v-col class="text-subtitle-1 text-center" cols="12">
        Loading ...
      </v-col>
      <v-col cols="6">
        <v-progress-linear
          color="primary"
          indeterminate
          rounded
          height="6"
        ></v-progress-linear>
      </v-col>
    </v-row>

    <p class="no-print" v-if="state.noData">No Data</p>

    <main
      class="report"
      v-if="!state.noData && !state.loading"
      id="section-print"
    >
      <div class="header">
        <h2 class="report-title">LAPORAN PEMOTONGAN</h2>
        <h3 class="period-title">
          Periode: {{ formatDate(formData.tanggal_awal) }} s/d
          {{ formatDate(formData.tanggal_akhir) }}
        </h3>
        <h3 class="period-title">SPK: {{ selectedSpkLabel }}</h3>
      </div>

      <div class="summary">
        <table>
          <tr>
            <td>Total Pemotongan</td>
            <td>:</td>
            <td>
              <b>{{ formatCurrency(data.grand_total_pemotongan) }}</b>
            </td>
          </tr>
          <tr>
            <td>Jumlah Data</td>
            <td>:</td>
            <td>{{ data.grand_count }}</td>
          </tr>
        </table>
      </div>

      <table width="100%" class="table-border mt-3">
        <thead>
          <tr>
            <th>Tgl Nota</th>
            <th>Nomor Nota</th>
            <th>Tgl Lunas</th>
            <th>SPK</th>
            <th>Kategori Pemotongan</th>
            <th class="text-right">Jumlah</th>
            <th>Keterangan</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in data.items" :key="item.id">
            <td>{{ formatDate(item.tgl_nota) }}</td>
            <td>{{ item.nomor_nota || "-" }}</td>
            <td>{{ formatDate(item.tgl_lunas) }}</td>
            <td>{{ selectedItemSpkLabel(item.port_awal) }}</td>
            <td>{{ item.kategori_pemotongan || "-" }}</td>
            <td class="text-right">{{ formatCurrency(item.total_pemotongan) }}</td>
            <td>{{ item.keterangan_pemotongan || "-" }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="5" class="text-right"><b>Total:</b></td>
            <td class="text-right"><b>{{ formatCurrency(data.grand_total_pemotongan) }}</b></td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </main>

    <v-btn
      class="mx-2 no-print"
      fab
      dark
      right
      bottom
      fixed
      color="success"
      v-if="!state.noData && !state.loading"
      @click="printDocument"
    >
      Print
    </v-btn>
  </div>
</template>

<script>
import axios from "axios";
import { formatDateOnly } from "@/util/helpers";

function toNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

export default {
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      state: {
        loading: false,
        noData: true
      },
      spkOptions: [
        { text: "Pontianak → Jakarta", value: "pontianak_jakarta" },
        { text: "Jakarta → Pontianak", value: "jakarta_pontianak" },
        { text: "Declaring", value: "declaring" }
      ],
      formData: {
        tanggal_awal: this.formatInputDate(firstDay),
        tanggal_akhir: this.formatInputDate(today),
        spk: null
      },
      data: {
        items: [],
        per_kategori: [],
        grand_total_pemotongan: 0,
        grand_count: 0
      }
    };
  },
  methods: {
    printDocument() {
      window.print();
    },
    formatInputDate(dateValue) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, "0");
      const day = String(dateValue.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async fetchLaporan() {
      if (!this.formData.tanggal_awal || !this.formData.tanggal_akhir) {
        return;
      }

      this.state.loading = true;

      await axios
        .get(`invoice/laporan-pemotongan`, {
          params: {
            tanggal_awal: this.formData.tanggal_awal,
            tanggal_akhir: this.formData.tanggal_akhir,
            spk: this.formData.spk
          }
        })
        .then(res => {
          const payload = res.data || {};
          const items = payload.items || [];

          this.data = {
            items,
            per_kategori: payload.per_kategori || [],
            grand_total_pemotongan:
              toNumber(payload.grand_total_pemotongan) ||
              items.reduce(
                (acc, item) => acc + toNumber(item.total_pemotongan),
                0
              ),
            grand_count: toNumber(payload.grand_count) || items.length
          };
          this.state.noData = items.length === 0;
          this.state.loading = false;
        })
        .catch(err => {
          this.state.loading = false;
          this.state.noData = true;
          this.data = {
            items: [],
            per_kategori: [],
            grand_total_pemotongan: 0,
            grand_count: 0
          };
          alert(err.response?.data?.message || "Gagal memuat laporan");
        });
    },
    formatDate(date) {
      return formatDateOnly(date, "/");
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("id-ID").format(toNumber(value));
    },
    routeLabel(port) {
      const value = String(port || "").toLowerCase();
      if (value.includes("pontianak") || value.includes("ptk")) return "Pontianak → Jakarta";
      if (value.includes("jakarta") || value.includes("jkt")) return "Jakarta → Pontianak";
      return port || "-";
    },
    selectedItemSpkLabel(port) {
      return this.formData.spk
        ? this.spkOptions.find(item => item.value === this.formData.spk)?.text || "-"
        : this.routeLabel(port);
    }
  },
  computed: {
    selectedSpkLabel() {
      const selected = this.spkOptions.find(item => item.value === this.formData.spk);
      return selected ? selected.text : "Semua SPK";
    }
  },
  created() {
    this.fetchLaporan();
  }
};
</script>

<style scoped>
.report {
  background-color: white;
  color: black;
  font-family: times;
  padding: 20px;
  width: 100%;
}

.header {
  margin-bottom: 12px;
}

.report-title,
.period-title {
  margin-bottom: 2px;
}

.period-title {
  font-style: italic;
}

.summary table {
  min-width: 320px;
}


.table-border,
.table-border th,
.table-border td {
  border: 1px solid #a8a8a8;
  border-collapse: collapse;
}

.table-border th,
.table-border td {
  padding: 4px 6px;
}

.table-border th {
  text-align: left;
}

@media print {
  .no-print {
    display: none !important;
  }
}
</style>
